import * as RadixDropdown from '@radix-ui/react-dropdown-menu';
import { trackMiscAction, trackSceneAction } from 'analytics';
import Icon, { IconType } from 'components/Icon';
import ShareModal from 'components/Modal/ShareModal';
import DownloadsMenuItem from 'components/Widget/DownloadsMenuItem';
import DropdownMenu from 'components/Widget/DropdownMenu';
import ExportMenuItem from 'components/Widget/ExportMenuItem';
import { Tooltip } from 'components/Widget/Tooltip';
import { MAX_LLM_EXPORT_FRAMES } from 'dragonmouse-shared/api/export';
import { Frame, FrameStatus } from 'dragonmouse-shared/interfaces';
import { MenuItemAction, MenuItemDivider } from 'menu/MenuItem';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { selectActiveScene, selectFrame, selectFrames } from 'redux/db/reducers';
import { canEditFrame } from 'redux/db/util';
import {
	showExportFramesAiModal,
	showExportFramesAssetsModal,
	showExportFramesCodeModal,
	zoomSetAroundCanvasCenter,
} from 'redux/mode/actions';
import { executeCommand } from 'redux/mode/commandManager';
import { UpdateFrameCommand } from 'redux/mode/commands';
import { selectTransform } from 'redux/mode/reducers';
import { getSelectedFrameId } from 'redux/mode/util';
import { RootReducer, selectSelectedFrames } from 'redux/reducers';
import store from 'redux/store';
import { useTheme } from 'styled-components';
import { unthemed } from 'tokens';
import * as Island from '../styles';
import * as S from './styles';

const TopRight = () => {
	const theme = useTheme();
	const tooltipId = useId();
	const [showShareSceneModal, setShowShareSceneModal] = useState(false);
	const [showShareFrameModal, setShowShareFrameModal] = useState(false);
	const [isRenaming, setIsRenaming] = useState(false);
	const inputRef = useRef<HTMLSpanElement | null>(null);
	const renameValueRef = useRef('');
	const centeredFrameRef = useRef<{ id: string; title: string } | null>(null);

	// Auto-open share modal when ?share=open is in the URL
	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		if (params.get('share') === 'open') {
			setShowShareSceneModal(true);
			params.delete('share');
			const newUrl = params.toString()
				? `${window.location.pathname}?${params}`
				: window.location.pathname;
			window.history.replaceState({}, '', newUrl);
		}
	}, []);

	const { scene, selected, selectedFrames, allFrames, scale, centeredFrameId } = useSelector(
		(r: RootReducer) => ({
			scene: selectActiveScene(r.db, r.mode.activeSceneId),
			selected: r.mode.selected,
			selectedFrames: selectSelectedFrames(r),
			allFrames: selectFrames(r.db),
			scale: selectTransform(r.mode).scale,
			centeredFrameId: r.mode.centeredFrameId,
		}),
		shallowEqual
	);

	const selectedFrameId = getSelectedFrameId(selected);
	const isOneFrameSelected = selectedFrames.length === 1;
	const isFocusMode = centeredFrameId !== null;

	const centeredFrame = useSelector((r: RootReducer) =>
		centeredFrameId ? selectFrame(r.db, centeredFrameId) : null
	);

	const exportFrames: Frame[] =
		isFocusMode && centeredFrame
			? [centeredFrame]
			: selectedFrames.length > 0
			? selectedFrames
			: allFrames;
	const unreadyFrames = exportFrames.filter((f) => f.metadata.status !== FrameStatus.READY);

	const exportDisabledReason = (kind: 'AI' | 'Code' | 'Assets'): string | undefined => {
		if (unreadyFrames.length > 0) {
			if (isFocusMode) return 'This frame is still generating.';
			if (selectedFrames.length > 0) {
				return `${unreadyFrames.length} of ${selectedFrames.length} selected frames are still generating.`;
			}
			return 'Some frames are still generating. Select the frames you want to export.';
		}
		if (kind !== 'Assets' && exportFrames.length > MAX_LLM_EXPORT_FRAMES) {
			const target = kind === 'AI' ? 'for AI' : 'code';
			if (selectedFrames.length === 0) {
				return `Select up to ${MAX_LLM_EXPORT_FRAMES} frames to export ${target}.`;
			}
			return `You've selected ${selectedFrames.length} frames. ${kind} export is limited to ${MAX_LLM_EXPORT_FRAMES} frames. Narrow your selection to export ${target}.`;
		}
		return undefined;
	};

	const frameTitle = centeredFrame?.title ?? 'Untitled Design';

	useEffect(() => {
		if (!isFocusMode && isRenaming) {
			const frame = centeredFrameRef.current;
			const trimmed = renameValueRef.current.trim();
			if (frame && trimmed && trimmed !== frame.title) {
				trackMiscAction('rename_frame');
				executeCommand(
					new UpdateFrameCommand(
						{ id: frame.id, title: frame.title },
						{ id: frame.id, title: trimmed }
					)
				);
			}
			centeredFrameRef.current = null;
			setIsRenaming(false);
		}
	}, [isFocusMode, isRenaming]);

	//----------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------
	const onSharePress = () => {
		if (isFocusMode && centeredFrameId) {
			trackMiscAction('open_frame_share_modal');
			setShowShareFrameModal(true);
		} else if (isOneFrameSelected) {
			trackMiscAction('open_frame_share_modal');
			setShowShareFrameModal(true);
		} else {
			trackSceneAction('open_share_modal');
			setShowShareSceneModal(true);
		}
	};

	const [exportOpen, setExportOpen] = useState(false);

	const onExportAssets = useCallback(() => {
		trackMiscAction('open_export_frames_assets_modal');
		const fixedFrameIds = isFocusMode && centeredFrameId ? [centeredFrameId] : undefined;
		store.dispatch(showExportFramesAssetsModal({ fixedFrameIds }));
	}, [isFocusMode, centeredFrameId]);

	const onExportCode = useCallback(() => {
		trackMiscAction('open_export_frames_code_modal');
		const fixedFrameIds = isFocusMode && centeredFrameId ? [centeredFrameId] : undefined;
		store.dispatch(showExportFramesCodeModal({ fixedFrameIds }));
	}, [isFocusMode, centeredFrameId]);

	const onExportAi = useCallback(() => {
		trackMiscAction('open_export_frames_ai_modal');
		const fixedFrameIds = isFocusMode && centeredFrameId ? [centeredFrameId] : undefined;
		store.dispatch(showExportFramesAiModal({ fixedFrameIds }));
	}, [isFocusMode, centeredFrameId]);

	const onTitleMouseDown = (e: React.MouseEvent) => {
		if (!centeredFrame || !canEditFrame(centeredFrame)) return;
		e.preventDefault();
		renameValueRef.current = centeredFrame.title;
		centeredFrameRef.current = { id: centeredFrame.id, title: centeredFrame.title };
		setIsRenaming(true);
	};

	const onRenameMount = useCallback((el: HTMLSpanElement | null) => {
		inputRef.current = el;
		if (!el) return;
		el.textContent = renameValueRef.current;
		el.focus();
		const range = document.createRange();
		range.selectNodeContents(el);
		const sel = window.getSelection();
		sel?.removeAllRanges();
		sel?.addRange(range);
	}, []);

	const onRenameInput = () => {
		renameValueRef.current = inputRef.current?.textContent ?? '';
	};

	const onRenameSubmit = () => {
		const frame = centeredFrameRef.current;
		const trimmed = renameValueRef.current.trim();
		centeredFrameRef.current = null;
		setIsRenaming(false);
		if (!frame || !trimmed) return;
		if (trimmed !== frame.title) {
			trackMiscAction('rename_frame');
			executeCommand(
				new UpdateFrameCommand(
					{ id: frame.id, title: frame.title },
					{ id: frame.id, title: trimmed }
				)
			);
		}
	};

	const onRenameKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			onRenameSubmit();
		} else if (e.key === 'Escape') {
			setIsRenaming(false);
		}
	};

	const zoomMenuItems = useMemo(
		() => [
			MenuItemAction.createZoomIn(),
			MenuItemAction.createZoomOut(),
			new MenuItemDivider(),
			new MenuItemAction('Zoom to 25%', '', async () => {
				store.dispatch(zoomSetAroundCanvasCenter(0.25));
			}),
			new MenuItemAction('Zoom to 50%', '', async () => {
				store.dispatch(zoomSetAroundCanvasCenter(0.5));
			}),
			MenuItemAction.createZoomReset(),
			new MenuItemAction('Zoom to 200%', '', async () => {
				store.dispatch(zoomSetAroundCanvasCenter(2.0));
			}),
			new MenuItemDivider(),
			MenuItemAction.createZoomToFitCanvas(),
			MenuItemAction.createZoomToFitSelection(),
		],
		[]
	);

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------
	const iconSize = 14;
	const shareFrameId = isFocusMode ? centeredFrameId : selectedFrameId;

	const renderExportButton = () => (
		<RadixDropdown.Root open={exportOpen} onOpenChange={setExportOpen}>
			<RadixDropdown.Trigger asChild>
				<S.ExportButton
					type="button"
					aria-label="Export"
					data-tooltip-id={isFocusMode ? tooltipId : undefined}
					data-tooltip-content={isFocusMode ? 'Export' : undefined}
				>
					{isFocusMode ? (
						<Island.IconWrapper>
							<Icon
								type={IconType.EXPORT_TRAY}
								width={iconSize}
								height={iconSize}
								color={theme.gray[0]}
							/>
						</Island.IconWrapper>
					) : (
						<S.ButtonLabel color={theme.gray[0]}>Export</S.ButtonLabel>
					)}
					<S.Chevron $open={exportOpen}>
						<Icon type={IconType.FILLED_ARROW_DOWN} width={12} height={12} color={theme.gray[0]} />
					</S.Chevron>
				</S.ExportButton>
			</RadixDropdown.Trigger>
			<RadixDropdown.Portal>
				<S.ExportDropdownContent side="bottom" sideOffset={8} align="end">
					<ExportMenuItem
						icon={IconType.GENERATE}
						label="Export for AI"
						description=".MD for Claude Code, ChatGPT, Codex…"
						onSelect={onExportAi}
						disabledReason={exportDisabledReason('AI')}
						tooltipId={tooltipId}
					/>
					<ExportMenuItem
						icon={IconType.IMAGE}
						label="Export Assets"
						description="PNG, Figma SVG"
						onSelect={onExportAssets}
						disabledReason={exportDisabledReason('Assets')}
						tooltipId={tooltipId}
					/>
					<ExportMenuItem
						icon={IconType.BRACKET}
						label="Export Code"
						description="React, Next.js, HTML…"
						onSelect={onExportCode}
						disabledReason={exportDisabledReason('Code')}
						tooltipId={tooltipId}
					/>
					<DownloadsMenuItem />
				</S.ExportDropdownContent>
			</RadixDropdown.Portal>
		</RadixDropdown.Root>
	);

	const renderShareButton = () => (
		<S.ShareButton
			type="button"
			aria-label="Share"
			onClick={onSharePress}
			data-tooltip-id={isFocusMode ? tooltipId : undefined}
			data-tooltip-content={isFocusMode ? 'Share' : undefined}
		>
			{isFocusMode ? (
				<Island.IconWrapper>
					<Icon
						type={IconType.SHARE_NODES}
						width={iconSize}
						height={iconSize}
						color={unthemed.gray[0]}
					/>
				</Island.IconWrapper>
			) : (
				<S.ButtonLabel color={unthemed.gray[0]}>Share</S.ButtonLabel>
			)}
		</S.ShareButton>
	);

	const renderFrameControls = () => (
		<Island.ControlButton
			active={true}
			buttonType={Island.ButtonType.SINGLE}
			onMouseDown={isRenaming ? undefined : onTitleMouseDown}
		>
			{isRenaming ? (
				<S.RenameInput
					ref={onRenameMount}
					contentEditable
					suppressContentEditableWarning
					onInput={onRenameInput}
					onBlur={onRenameSubmit}
					onKeyDown={onRenameKeyDown}
				/>
			) : (
				<S.TitleText>{frameTitle}</S.TitleText>
			)}
		</Island.ControlButton>
	);

	const renderZoomControls = () => (
		<DropdownMenu menuItems={zoomMenuItems}>
			<Island.ControlButton active={true} buttonType={Island.ButtonType.SINGLE}>
				<Island.Label color={theme.gray[1000]}>{Math.round(scale * 100)}%</Island.Label>
				<Icon type={IconType.FILLED_ARROW_DOWN} width={10} height={10} color={theme.gray[1000]} />
			</Island.ControlButton>
		</DropdownMenu>
	);

	return (
		<>
			{isRenaming && <S.RenameOverlay onMouseDown={onRenameSubmit} />}
			<Island.TopRight>
				{isFocusMode ? renderFrameControls() : renderZoomControls()}
				{renderExportButton()}
				{renderShareButton()}
				<Tooltip id={tooltipId} placement="bottom" />
			</Island.TopRight>
			{showShareSceneModal && scene && (
				<ShareModal
					kind="scene"
					entityId={scene.id}
					onClose={() => setShowShareSceneModal(false)}
				/>
			)}
			{showShareFrameModal && shareFrameId && (
				<ShareModal
					kind="frame"
					entityId={shareFrameId}
					onClose={() => setShowShareFrameModal(false)}
				/>
			)}
		</>
	);
};

export default TopRight;
